<!-- src/components/ForecastWeekInput.vue -->
<template>
  <div class="card core-card core-section">
    <div class="card-content">
      <span class="card-title">
        Week Ending:
        {{ formatUK(new Date(week.weekEndingDateISO)) }}
      </span>

      <div v-if="lockMessage" class="core-feedback core-feedback--error">
        {{ lockMessage }}
      </div>

      <div class="responsive-table hide-on-small-only" style="margin-top: 10px">
        <table class="highlight core-table forecast-table">
          <thead>
            <tr>
              <th>Category</th>

              <th v-for="(d, i) in days" :key="'h-' + i">
                <span class="day-name">
                  {{ d }}
                </span>

                <br />

                <span class="day-date grey-text text-darken-1">
                  {{ formatUK(week.dates[i]) }}
                </span>
              </th>

              <th>Total for Week</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td class="core-strong">Total (for each day)</td>

              <td v-for="(t, i) in dayTotals" :key="'dt-' + i" class="core-strong">
                {{ formatGBP(t) }}
              </td>

              <td class="core-strong">
                {{ formatGBP(weekTotalAll) }}
              </td>
            </tr>

            <tr v-for="cat in categories" :key="cat">
              <td class="cat-name">
                {{ labelForCategory(cat) }}
              </td>

              <td v-for="(_, di) in 7" :key="cat + '-' + di">
                <input
                  type="number"
                  step="1"
                  class="browser-default forecast-input"
                  :disabled="!editable || busy"
                  :value="week.rows[cat][di]"
                  @input="onInput(cat, di, $event.target.value)"
                />
              </td>

              <td class="cat-week-total">
                {{ formatGBP(rowTotal(cat)) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="show-on-small hide-on-med-and-up" style="margin-top: 10px">
        <div class="responsive-table">
          <table class="highlight core-table mobile-forecast">
            <thead>
              <tr>
                <th>Category</th>
                <th>Total</th>
                <th>Liquor</th>
                <th>Food</th>
                <th>Door</th>
                <th>Other</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(d, i) in days" :key="'mday-' + i">
                <td class="mobile-day">
                  <div class="mobile-day-name">
                    {{ d }}
                  </div>

                  <div class="mobile-day-date grey-text text-darken-1">
                    {{ formatUK(week.dates[i]) }}
                  </div>
                </td>

                <td class="core-strong">
                  {{ formatGBP(dayTotals[i]) }}
                </td>

                <td>
                  <input
                    type="number"
                    step="1"
                    class="browser-default forecast-input forecast-input--mobile"
                    :disabled="!editable || busy"
                    :value="week.rows.liquor[i]"
                    @input="onInput('liquor', i, $event.target.value)"
                  />
                </td>

                <td>
                  <input
                    type="number"
                    step="1"
                    class="browser-default forecast-input forecast-input--mobile"
                    :disabled="!editable || busy"
                    :value="week.rows.food[i]"
                    @input="onInput('food', i, $event.target.value)"
                  />
                </td>

                <td>
                  <input
                    type="number"
                    step="1"
                    class="browser-default forecast-input forecast-input--mobile"
                    :disabled="!editable || busy"
                    :value="week.rows.door[i]"
                    @input="onInput('door', i, $event.target.value)"
                  />
                </td>

                <td>
                  <input
                    type="number"
                    step="1"
                    class="browser-default forecast-input forecast-input--mobile"
                    :disabled="!editable || busy"
                    :value="week.rows.other[i]"
                    @input="onInput('other', i, $event.target.value)"
                  />
                </td>
              </tr>

              <tr>
                <td class="core-strong">Total for week</td>

                <td class="core-strong">
                  {{ formatGBP(weekTotalAll) }}
                </td>

                <td class="core-strong">
                  {{ formatGBP(rowTotal("liquor")) }}
                </td>

                <td class="core-strong">
                  {{ formatGBP(rowTotal("food")) }}
                </td>

                <td class="core-strong">
                  {{ formatGBP(rowTotal("door")) }}
                </td>

                <td class="core-strong">
                  {{ formatGBP(rowTotal("other")) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="core-actions" style="margin-top: 14px">
        <button class="btn" :disabled="!canSubmit || busy" @click="onSubmit">
          Submit week
        </button>

        <button class="btn grey" :disabled="!canAdjust || busy" @click="onAdjust">
          Adjust forecast
        </button>

        <button
          v-if="editable && submitted"
          class="btn red lighten-1"
          :disabled="busy"
          @click="onStopAdjust"
        >
          Stop adjusting
        </button>

        <span v-if="submitted" class="core-badge core-badge--success"> Submitted </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  week: {
    type: Object,
    required: true,
  },

  categories: {
    type: Array,
    required: true,
  },

  days: {
    type: Array,
    required: true,
  },

  formatUK: {
    type: Function,
    required: true,
  },

  editable: {
    type: Boolean,
    required: true,
  },

  submitted: {
    type: Boolean,
    required: true,
  },

  canSubmit: {
    type: Boolean,
    required: true,
  },

  canAdjust: {
    type: Boolean,
    required: true,
  },

  busy: {
    type: Boolean,
    default: false,
  },

  lockMessage: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["cell", "submit", "adjust", "stop-adjust"]);

const CATEGORY_LABELS = {
  liquor: "Liquor",
  food: "Food",
  door: "Door",
  other: "Other",
};

function labelForCategory(cat) {
  return CATEGORY_LABELS[cat] || cat;
}

function asNumber(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function formatGBP(value) {
  return asNumber(value).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function onInput(category, dayIndex, raw) {
  emit("cell", {
    category,
    dayIndex,
    value: asNumber(raw),
  });
}

function rowTotal(category) {
  const arr = props.week?.rows?.[category] || [];

  return arr.reduce((sum, v) => sum + asNumber(v), 0);
}

const dayTotals = computed(() => {
  const totals = Array(7).fill(0);

  for (const cat of props.categories) {
    const arr = props.week?.rows?.[cat] || [];

    for (let i = 0; i < 7; i++) {
      totals[i] += asNumber(arr[i]);
    }
  }

  return totals;
});

const weekTotalAll = computed(() =>
  dayTotals.value.reduce((sum, v) => sum + asNumber(v), 0)
);

function onSubmit() {
  if (props.busy || !props.canSubmit) {
    return;
  }

  emit("submit");
}

function onAdjust() {
  if (props.busy || !props.canAdjust) {
    return;
  }

  emit("adjust");
}

function onStopAdjust() {
  if (props.busy) {
    return;
  }

  emit("stop-adjust");
}
</script>

<style scoped>
.forecast-table,
.mobile-forecast {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.forecast-table th,
.forecast-table td,
.mobile-forecast th,
.mobile-forecast td {
  vertical-align: middle;
  white-space: normal;
  word-break: break-word;
}

.cat-name,
.cat-week-total {
  font-weight: 600;
}

.day-name {
  font-weight: 700;
}

.day-date {
  font-size: 12px;
}

.forecast-input {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
  padding: 2px 6px;
}

.mobile-forecast th,
.mobile-forecast td {
  padding: 8px 6px;
  font-size: 13px;
}

.mobile-day-name {
  font-weight: 700;
}

.mobile-day-date {
  font-size: 11px;
  line-height: 1.2;
}

.forecast-input--mobile {
  width: 100% !important;
  box-sizing: border-box;
  padding: 2px 4px;
  font-size: 13px;
}

@media (min-width: 601px) and (max-width: 992px) {
  .forecast-table th,
  .forecast-table td {
    padding: 6px;
    font-size: 13px;
  }

  .day-date {
    font-size: 11px;
  }

  .forecast-input {
    padding: 2px 4px;
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .mobile-forecast {
    min-width: 620px;
  }

  .responsive-table {
    margin-left: -2px;
    margin-right: -2px;
  }
}
</style>
